import { PanelRegistryStore } from '@shared/ui';
import { Injectable } from '@angular/core';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { defer, Observable, of } from 'rxjs';
import { concatLatestFrom } from '@ngrx/operators';

export interface MosaicGalleryPanelDelegate {
  selectedItems$: Observable<Set<string>>;

  clearSelection(): void;
  filterByLabel(label: string): void;
  filterByUnlabeled(): void;
  filterByAsset(assetId: string): void;
  selectRois(roiIds: string[]): void;
  filterByChanged(): void;
  filterBySelected(): void;
  addLabels(labels: string[]): void;
  removeAllLabelsOnSelectedItems(): void;
}

@Injectable()
export class MosaicGalleryPanels extends PanelRegistryStore<MosaicGalleryPanelDelegate> {
  private static readonly MAX_NUM_PANELS = 5;

  constructor() {
    super({
      panels: { entities: { 0: { id: 0 } }, ids: [0] },
      focusedPanel: 0,
      maxNumPanels: MosaicGalleryPanels.MAX_NUM_PANELS,
    });
  }

  public readonly focusedPanelSelectedItems$ = this.focusedPanelRef$.pipe(
    switchMap((ref) => {
      if (!ref) return of(new Set<string>());
      return ref.selectedItems$;
    })
  );

  public readonly clearAllPanelSelections = this.effect((action$: Observable<void>) =>
    action$.pipe(
      concatLatestFrom(() => this.panels$),
      map(([, panels]) => {
        for (const panelId in panels) panels[panelId].ref?.clearSelection();
      })
    )
  );

  public readonly clearFocusedSelection = this.effect((action$: Observable<void>) =>
    action$.pipe(
      concatLatestFrom(() => this.focusedPanelRef$),
      map(([, ref]) => ref?.clearSelection())
    )
  );

  public readonly filterFocusedPanelByLabel = this.effect((label$: Observable<string>) =>
    label$.pipe(
      concatLatestFrom(() => this.focusedPanelRef$),
      map(([label, ref]) => ref?.filterByLabel(label))
    )
  );

  public readonly filterFocusedPanelByUnlabeled = this.effect((action$: Observable<void>) =>
    action$.pipe(
      concatLatestFrom(() => this.focusedPanelRef$),
      map(([, ref]) => ref?.filterByUnlabeled())
    )
  );

  public readonly filterFocusedPanelByAsset = this.effect((assetId$: Observable<string>) =>
    assetId$.pipe(
      concatLatestFrom(() => this.focusedPanelRef$),
      map(([assetId, ref]) => ref?.filterByAsset(assetId))
    )
  );

  public readonly filterFocusedPanelByRoiIds = this.effect((roiIds$: Observable<string[]>) =>
    roiIds$.pipe(
      concatLatestFrom(() => this.focusedPanelRef$),
      map(([roiIds, ref]) => ref?.selectRois(roiIds))
    )
  );

  public readonly filterFocusedPanelByChanged = this.effect((action$: Observable<void>) =>
    action$.pipe(
      concatLatestFrom(() => this.focusedPanelRef$),
      map(([, ref]) => ref?.filterByChanged())
    )
  );

  public readonly filterFocusedPanelBySelected = this.effect((action$: Observable<void>) =>
    action$.pipe(
      concatLatestFrom(() => this.focusedPanelRef$),
      map(([, ref]) => ref?.filterBySelected())
    )
  );

  public readonly addLabelsToFocusedItems = this.effect((labels$: Observable<string[]>) =>
    labels$.pipe(
      concatLatestFrom(() => this.focusedPanelRef$),
      map(([labels, ref]) => ref?.addLabels(labels))
    )
  );

  public readonly removeAllLabelsOnFocusedItems = this.effect((action$: Observable<void>) =>
    action$.pipe(
      concatLatestFrom(() => this.focusedPanelRef$),
      map(([, ref]) => ref?.removeAllLabelsOnSelectedItems())
    )
  );

  public readonly openNewPanelWithLabel = this.effect((label$: Observable<string>) =>
    label$.pipe(switchMap((label) => this.awaitNewPanelRef().pipe(tap((ref) => ref.filterByLabel(label)))))
  );

  public readonly openNewPanelWithUnlabeled = this.effect((action$: Observable<void>) =>
    action$.pipe(switchMap(() => this.awaitNewPanelRef().pipe(tap((ref) => ref.filterByUnlabeled()))))
  );

  public readonly openNewPanelWithChanged = this.effect((action$: Observable<void>) =>
    action$.pipe(switchMap(() => this.awaitNewPanelRef().pipe(tap((ref) => ref.filterByChanged()))))
  );

  public readonly openNewPanelWithSelected = this.effect((action$: Observable<void>) =>
    action$.pipe(switchMap(() => this.awaitNewPanelRef().pipe(tap((ref) => ref.filterBySelected()))))
  );

  public readonly trackByPanelId = (_index: number, panelId: number): number => panelId;

  private awaitNewPanelRef(): Observable<MosaicGalleryPanelDelegate> {
    return defer(() => {
      this.addPanel();
      const ids = this.get().panels.ids as number[];
      const newPanelId = ids[ids.length - 1];
      this.focusPanel(newPanelId);

      return this.select((state) => state.panels.entities[newPanelId]?.ref).pipe(
        filter((ref): ref is MosaicGalleryPanelDelegate => !!ref),
        take(1)
      );
    });
  }
}
